import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { getCategory } from "../lib/api";
import { useLanguage } from "../context/LanguageContext";
import { Tx } from "../context/AutoTranslateContext";

const Category = () => {
  const { categoryId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t, lang } = useLanguage();

  useEffect(() => {
    setLoading(true);
    // Silence 404s — the UI already handles the "not found" state via `!data`.
    getCategory(categoryId, lang)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [categoryId, lang]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-20">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-cream rounded w-1/3" />
          <div className="h-32 bg-cream rounded-2xl" />
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-20 text-center">
        <p className="text-mute"><Tx>Category not found.</Tx></p>
        <Link to="/" className="text-forest underline mt-4 inline-block">{t("home")}</Link>
      </div>
    );
  }

  const { category, illnesses } = data;

  return (
    <div data-testid="category-page" className="max-w-7xl mx-auto px-4 sm:px-8 py-12 sm:py-20">
      <Link to="/" data-testid="back-link" className="inline-flex items-center gap-2 text-mute hover:text-forest text-sm mb-8 transition">
        <ArrowLeft className="w-4 h-4 rtl:rotate-180" />
        {t("all_categories")}
      </Link>

      <div className="mb-12">
        <p className="arabic text-gold text-2xl mb-3">{category.arabic}</p>
        <h1 data-testid="category-title" className="font-heading text-4xl sm:text-5xl lg:text-6xl text-forest font-bold tracking-tight mb-4">
          {lang === "ar" ? category.arabic : category.name}
        </h1>
        <p className="text-mute leading-relaxed max-w-2xl text-lg">{category.description}</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 stagger">
        {illnesses.map((illness) => (
          <Link
            key={illness.id}
            to={`/illness/${illness.id}`}
            data-testid={`illness-card-${illness.id}`}
            className="clay-surface rounded-2xl p-6 lift-on-hover group"
          >
            <p className="arabic text-gold text-lg mb-2">{illness.arabic_name}</p>
            <h3 className="font-heading text-2xl text-forest font-semibold mb-3 group-hover:text-sage transition">
              {lang === "ar" ? illness.arabic_name : illness.name}
            </h3>
            <p className="text-sm text-mute leading-relaxed line-clamp-3 mb-4">{illness.summary}</p>
            <span className="inline-flex items-center gap-1 text-xs text-forest font-medium">
              {t("read_full")}
              <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 rtl:rotate-180 transition" />
            </span>
          </Link>
        ))}
      </div>

      {illnesses.length === 0 && (
        <div className="text-center py-20 text-mute">{t("no_illness_in_category")}</div>
      )}
    </div>
  );
};

export default Category;
